import { useMemo } from "react";
import {
  AlertCircle,
  CheckSquare,
  GraduationCap,
  LineChart,
} from "lucide-react";
import {
  buildClassStageActions,
  buildClassStageDonutList,
  buildClassStageHeroCopy,
  buildClassStageJourneyRows,
  buildGenderChartRows,
  buildStageDonutStyle,
  buildStageKpiCards,
  buildTransitionFlowSteps,
  deriveClassStatus,
  findPeakClass,
  fmt,
  formatLakhShort,
  pct,
  statusTagClass,
} from "@/utils/enrolmentDerived";
import "./EnrolmentClassStageTab.css";

const ACTION_ICONS = {
  red: LineChart,
  orange: AlertCircle,
  green: CheckSquare,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function EnrolmentClassStageTab({ overview = {}, classData = [] }) {
  const peakClass = useMemo(() => findPeakClass(classData), [classData]);
  const hero = useMemo(
    () => buildClassStageHeroCopy(overview, peakClass),
    [overview, peakClass]
  );
  const journeyRows = useMemo(() => buildClassStageJourneyRows(overview), [overview]);
  const kpiCards = useMemo(() => buildStageKpiCards(overview), [overview]);
  const genderRows = useMemo(() => buildGenderChartRows(classData), [classData]);
  const donutList = useMemo(() => buildClassStageDonutList(overview), [overview]);
  const donutStyle = useMemo(() => {
    const rows = donutList.map((item) => ({
      key: item.label,
      percentage: item.pct,
      color: item.color,
    }));
    return buildStageDonutStyle(rows);
  }, [donutList]);
  const flowSteps = useMemo(() => buildTransitionFlowSteps(overview), [overview]);
  const actions = useMemo(
    () => buildClassStageActions(overview, classData),
    [overview, classData]
  );

  const tableRows = useMemo(
    () =>
      classData.map((cls) => ({
        ...cls,
        status: deriveClassStatus(cls, peakClass?.class_name),
      })),
    [classData, peakClass]
  );

  const girlsOverall = overview.girls_participation_pct ?? 0;

  return (
    <div className="enr-cs-tab">
      <section className="enr-cs-hero" id="enr-cs-hero">
        <div className="enr-cs-hero-layout">
          <div className="enr-cs-summary">
            <h2 className="dashboard-kpi-hero-title">{hero.title}</h2>
            <p>{hero.body}</p>
            <div className="enr-cs-strip">
              <div className="enr-cs-strip-card">
                <small>Largest Stage</small>
                <strong>{hero.largestStage}</strong>
                <span>Primary · Class 1–5</span>
              </div>
              <div className="enr-cs-strip-card">
                <small>Peak Class</small>
                <strong>{hero.peakClass}</strong>
                <span>{hero.peakClassLabel} enrolment</span>
              </div>
              <div className="enr-cs-strip-card">
                <small>Gender Mix</small>
                <strong>{hero.girlsShare}</strong>
                <span>Girls share overall</span>
              </div>
            </div>
            <div className="enr-cs-insight-note">
              <div className="enr-cs-icon-box purple">
                <GraduationCap size={22} />
              </div>
              <div>
                <b>Primary action for review</b>
                <span>{hero.primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="enr-cs-journey">
            <div className="enr-cs-journey-head">
              <div>
                <h3>Stage Distribution Journey</h3>
                <p>NEP-aligned stage grouping with share and total enrolment.</p>
              </div>
              <span className="enr-cs-pill">{fmt(overview.grand_total)} students</span>
            </div>
            <div className="enr-cs-journey-bars">
              {journeyRows.map((row) => (
                <div key={row.key} className="enr-cs-stage-row">
                  <div className="enr-cs-stage-label">
                    <strong>{row.label}</strong>
                    <span>{row.sub}</span>
                  </div>
                  <div className="enr-cs-stage-track">
                    <div
                      className={`enr-cs-stage-fill ${row.fillClass}`.trim()}
                      style={{ width: `${row.barWidth}%` }}
                    />
                  </div>
                  <div className="enr-cs-stage-value">
                    {fmt(row.total)}
                    <span>{pct(row.sharePct)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="enr-cs-kpis" aria-label="Stage KPI cards">
        {kpiCards.map((card) => (
          <article key={card.label} className={`enr-cs-kpi ${card.tone}`.trim()}>
            <small>{card.label}</small>
            <strong>{fmt(card.total)}</strong>
            <span>{card.caption}</span>
          </article>
        ))}
      </section>

      <div className="enr-cs-section-title" id="enr-cs-gender">
        <div>
          <h2>Class-Wise Gender Balance</h2>
          <p>
            The earlier single stacked chart is redesigned into readable horizontal bars with GPI
            badges. This reduces visual compression and makes low-balance classes easy to scan.
          </p>
        </div>
        <button type="button" className="enr-cs-section-action" onClick={() => scrollToId("enr-cs-table")}>
          View detailed table →
        </button>
      </div>

      <section className="enr-cs-analytics-grid">
        <article className="enr-cs-panel">
          <div className="enr-cs-panel-head">
            <div>
              <h3>Boys vs Girls by Class</h3>
              <p>Stacked share view across key classes with GPI signal.</p>
            </div>
            <span className="enr-cs-pill">Girls overall {pct(girlsOverall)}</span>
          </div>
          <div className="enr-cs-gender-chart">
            {genderRows.map((row) => (
              <div key={row.class_name} className="enr-cs-gender-row">
                <div className="enr-cs-class-name">{row.class_name}</div>
                <div className="enr-cs-stack-track">
                  <div className="enr-cs-boys" style={{ width: `${row.boysPct}%` }} />
                  <div className="enr-cs-girls" style={{ width: `${row.girlsPct}%` }} />
                </div>
                <div className={`enr-cs-gpi-badge ${row.gpiGood ? "good" : ""}`.trim()}>
                  GPI {row.gpi}
                </div>
              </div>
            ))}
          </div>
          <div className="enr-cs-chart-legend">
            <span>
              <i className="enr-cs-legend-dot" />
              Boys
            </span>
            <span>
              <i className="enr-cs-legend-dot pink" />
              Girls
            </span>
            <span>GPI closer to 1.00 indicates better parity</span>
          </div>
        </article>

        <article className="enr-cs-panel">
          <div className="enr-cs-panel-head">
            <div>
              <h3>Stage Share Composition</h3>
              <p>Compact donut view to understand where the student base is concentrated.</p>
            </div>
            <span className="enr-cs-pill">Stage mix</span>
          </div>
          <div className="enr-cs-donut-layout">
            <div className="enr-cs-donut" style={donutStyle} aria-label="Stage share donut">
              <div className="enr-cs-donut-center">
                <div>
                  <strong>{formatLakhShort(overview.grand_total)}</strong>
                  <small>Students</small>
                </div>
              </div>
            </div>
            <div className="enr-cs-donut-list">
              {donutList.map((item) => (
                <div key={item.label} className="enr-cs-donut-item">
                  <span className="enr-cs-donut-color" style={{ background: item.color }} />
                  <span>{item.label}</span>
                  <b>{pct(item.pct)}</b>
                </div>
              ))}
            </div>
          </div>
        </article>
      </section>

      <div className="enr-cs-section-title" id="enr-cs-retention">
        <div>
          <h2>Transition and Retention Pressure</h2>
          <p>
            Instead of showing only a large table, this section highlights the story: enrolment
            climbs through primary, stabilizes in middle classes, and drops after secondary.
          </p>
        </div>
        <button type="button" className="enr-cs-section-action" onClick={() => scrollToId("enr-cs-actions")}>
          Recommended actions →
        </button>
      </div>

      <section className="enr-cs-two-column">
        <article className="enr-cs-flow-card">
          <div className="enr-cs-panel-head">
            <div>
              <h3>Stage Progression View</h3>
              <p>Relative volume through each stage, normalized to Primary enrolment.</p>
            </div>
            <span className="enr-cs-pill">Transition view</span>
          </div>
          <div className="enr-cs-transition-flow">
            {flowSteps.map((step) => (
              <div key={step.key} className="enr-cs-flow-step">
                <div className="enr-cs-flow-step-content">
                  <small>{step.label}</small>
                  <strong>{step.relative}%</strong>
                  <span>{step.caption}</span>
                </div>
                <div className="enr-cs-flow-step-bar" aria-hidden="true">
                  <div className="enr-cs-flow-step-bar-track">
                    <i
                      className={step.barTone}
                      style={{ width: `${step.relative}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </article>

        <article className="enr-cs-panel" id="enr-cs-actions">
          <div className="enr-cs-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Action-oriented insights for district and block officers.</p>
            </div>
            <span className="enr-cs-pill">Priority</span>
          </div>
          <div className="enr-cs-action-list">
            {actions.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] || LineChart;
              return (
                <div key={item.title} className="enr-cs-action-item">
                  <div className={`enr-cs-icon-box ${item.iconClass}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <section className="enr-cs-panel enr-cs-table-card" id="enr-cs-table">
        <div className="enr-cs-table-head">
          <div>
            <h3>Class-Wise Detailed Breakdown</h3>
            <p>Cleaned table with status tags so officers can identify parity and transition issues quickly.</p>
          </div>
          <span className="enr-cs-pill">{classData.length} class groups</span>
        </div>
        <table className="enr-cs-table" aria-label="Class-wise detailed enrolment table">
          <thead>
            <tr>
              <th>Class</th>
              <th>Boys</th>
              <th>Girls</th>
              <th>Total</th>
              <th>Girls %</th>
              <th>GPI</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row) => {
              const gpiClass = row.gpi >= 0.95 ? "good" : "";
              const statusClass = statusTagClass(row.status);
              return (
                <tr key={row.class_name}>
                  <td>{row.class_name}</td>
                  <td className="number">{fmt(row.boys)}</td>
                  <td className="number">{fmt(row.girls)}</td>
                  <td className="number">{fmt(row.total)}</td>
                  <td>{pct(row.girls_pct)}</td>
                  <td>
                    <span className={`enr-cs-tag ${gpiClass}`.trim()}>{row.gpi}</span>
                  </td>
                  <td>
                    <span className={`enr-cs-tag ${statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
